import React, { useState } from 'react';
import BottomSheet from './ui/BottomSheet';
import { useDevice } from '../hooks/useDevice';
import { SignedIn, SignedOut, UserButton, SignInButton, useUser, useClerk } from '@clerk/clerk-react';
import ContractorBusinessTab from './UserProfileTabs/ContractorBusinessTab';
import PaymentsTab from './UserProfileTabs/PaymentsTab';
// CB:BEGIN fr-001-import-settings-tabs
import CoreBidsNotificationsTab from './UserProfileTabs/CoreBidsNotificationsTab';
import CoreBidsPrivacyTab from './UserProfileTabs/CoreBidsPrivacyTab';
import CoreBidsContractorPreferencesTab from './UserProfileTabs/CoreBidsContractorPreferencesTab';
// CB:END fr-001-import-settings-tabs
import { isDemoMode } from '../lib/featureFlags';
import { getApiUrl } from '../lib/getApiUrl';
import { useAuthFetch } from '../lib/useAuthFetch';
import { useContractorReadiness } from '../hooks/useContractorReadiness';

const DOC_TYPE_LABELS: Record<string, string> = {
  LICENSE: 'Contractor License',
  INSURANCE_COI: 'Insurance Certificate (COI)',
  W9: 'W-9',
  AGREEMENT_SIGNED_PDF: 'Platform Agreement',
  ACH_AUTHORIZATION: 'ACH Authorization',
  OTHER: 'Other',
};

interface SharedHeaderContractorProps {
  onNavigate: (screen: string) => void;
  showHelpButton?: boolean;
}

const SharedHeaderContractor: React.FC<SharedHeaderContractorProps> = ({ onNavigate, showHelpButton = false }) => {
  const { user } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const device = useDevice();
  const authFetch = useAuthFetch();
  // CB:BEGIN fr-001-userbutton-ref
  const userButtonContainerRef = React.useRef<HTMLDivElement>(null);
  
  // Function to open user profile modal directly (skip dropdown)
  const triggerUserButton = React.useCallback(() => {
    const manageAccountLink = document.querySelector('a[href*="account"], button[aria-label*="Manage account"], [data-clerk-element="manageAccount"]') as HTMLElement;
    if (manageAccountLink) {
      manageAccountLink.click();
      return;
    }
    
    requestAnimationFrame(() => {
      let clicked = false;
      
      if (userButtonContainerRef.current) {
        const buttons = userButtonContainerRef.current.querySelectorAll('button');
        for (const button of Array.from(buttons)) {
          if (button.disabled) continue;
          button.click();
          clicked = true;
          break;
        }
      }
      
      if (!clicked) {
        const container = document.querySelector('[data-user-button-container="true"]');
        if (container) {
          const button = container.querySelector('button') as HTMLButtonElement;
          if (button) {
            button.click();
            clicked = true;
          }
        }
      }
      
      if (clicked) {
        setTimeout(() => {
          const selectors = [
            'a[href*="account"]',
            'button[aria-label*="Manage account"]',
            '[data-clerk-element="manageAccount"]',
          ];
          
          for (const selector of selectors) {
            try {
              const link = document.querySelector(selector) as HTMLElement;
              if (link && link.offsetParent !== null) {
                link.click();
                return;
              }
            } catch (e) {}
          }
          
          const allLinks = document.querySelectorAll('a, button');
          for (const link of Array.from(allLinks)) {
            const text = link.textContent?.toLowerCase() || '';
            if ((text.includes('manage') || text.includes('account')) && link.offsetParent !== null) {
              (link as HTMLElement).click();
              return;
            }
          }
        }, 100);
      } else {
        openUserProfile();
      }
    });
  }, [openUserProfile]);
  // CB:END fr-001-userbutton-ref

  // CB:BEGIN unified-account-status
  const { accountReady, stripeConnected, verificationState, loading: readinessLoading } = useContractorReadiness({ clerkId: user?.id });

  const statusLabel = readinessLoading
    ? 'Loading...'
    : accountReady
      ? 'Account ready to bid'
      : 'Finish setup to start bidding';

  const statusTone = readinessLoading ? 'neutral' : accountReady ? 'success' : 'warning';
  // CB:END unified-account-status

  const handleSignOut = async () => {
    try {
      await signOut();
      onNavigate('landing');
    } catch (error) {
      console.error('Error signing out:', error);
      onNavigate('landing');
    }
  };

  // CB:BEGIN contractor-docs-modal
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [contractorDocs, setContractorDocs] = useState<any[]>([]);
  const [projectFolders, setProjectFolders] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  const openDocsModal = async () => {
    setShowDocsModal(true);
    setDocsLoading(true);
    try {
      const [verDocs, projDocs] = await Promise.all([
        authFetch(getApiUrl('/api/contractor/verification/self/documents')).then(r => r.ok ? r.json() : []),
        authFetch(getApiUrl('/api/contractor/verification/self/project-documents')).then(r => r.ok ? r.json() : []),
      ]);
      setContractorDocs(Array.isArray(verDocs) ? verDocs : []);
      setProjectFolders(Array.isArray(projDocs) ? projDocs.filter((f: any) => f.items?.length > 0) : []);
    } catch (e) {
      console.error('Failed to fetch contractor documents:', e);
    } finally {
      setDocsLoading(false);
    }
  };

  const viewContractorDoc = async (docId: string, kind?: string, signingId?: string) => {
    try {
      const url = kind === 'signing' && signingId
        ? getApiUrl(`/api/contractor/verification/self/project-documents/signing/${signingId}/download`)
        : getApiUrl(`/api/contractor/verification/self/documents/${docId}/download`);
      const r = await authFetch(url);
      if (!r.ok) return;
      const d = await r.json();
      if (d.url) window.open(d.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('Failed to open document:', e);
    }
  };

  const toggleProjectFolder = (projectId: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };
  // CB:END contractor-docs-modal

  const btnColors = {
    blue:   { base: '#6b7280', hover: '#4b5563' },
    indigo: { base: '#6366f1', hover: '#4f46e5' },
    green:  { base: '#059669', hover: '#047857' },
  };
  const solidBtn = (c: keyof typeof btnColors) => ({
    style: {
      fontSize: 14, padding: '7px 14px', color: 'white' as const,
      background: btnColors[c].base, border: 'none', fontWeight: 500,
      borderRadius: 6, cursor: 'pointer' as const, transition: 'background 0.15s',
      textDecoration: 'none' as const, whiteSpace: 'nowrap' as const,
    },
    onMouseEnter: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => { e.currentTarget.style.background = btnColors[c].hover; },
    onMouseLeave: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => { e.currentTarget.style.background = btnColors[c].base; },
  });

  // Create the contractor navigation buttons
  const contractorNavButtons = (
    <>
      <button {...solidBtn('blue')} onClick={() => onNavigate('contractor-dashboard')}>
        Home
      </button>
      <button {...solidBtn('blue')} onClick={() => onNavigate('contractor-active-projects')}>
        Active Jobs
      </button>

      {/* Search for Projects — high-visibility amber CTA */}
      <button
        style={{
          fontSize: 14, padding: '7px 16px', color: '#111827',
          background: '#f59e0b', border: 'none', fontWeight: 700,
          borderRadius: 6, cursor: 'pointer', transition: 'background 0.15s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#d97706'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = '#f59e0b'; }}
        onClick={() => onNavigate('current-bids')}
      >
        Search for Projects
      </button>

      <button {...solidBtn('green')} onClick={() => onNavigate('contractor-payments')}>
        Payments
      </button>

      {/* CB:BEGIN documents-button */}
      <button {...solidBtn('indigo')} onClick={openDocsModal}>
        Documents
      </button>
      {/* CB:END documents-button */}

      {/* CB:BEGIN settings-button */}
      <button {...solidBtn('blue')} onClick={triggerUserButton}>
        Settings
      </button>
      {/* CB:END settings-button */}

      {/* CB:BEGIN contact-us-button */}
      <a
        href={`mailto:support@corebids.com?subject=Project%20${encodeURIComponent(localStorage.getItem('active_project_id') || '')}`}
        {...solidBtn('blue')}
      >
        Contact Us
      </a>
      {/* CB:END contact-us-button */}

      {/* CB:BEGIN contractor-help-button */}
      {showHelpButton && (
        <button {...solidBtn('indigo')} onClick={() => {
          try { sessionStorage.setItem('cb.help.origin', 'contractor-dashboard'); } catch {}
          onNavigate('contractor-help');
        }}>
          Help
        </button>
      )}
      {/* CB:END contractor-help-button */}
    </>
  );

  // CB:BEGIN unified-status-badge
  const accountStatusBadge = (
    <span style={{
      padding: '4px 10px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      color: readinessLoading ? '#4b5563' : accountReady ? '#065f46' : '#991b1b',
      background: readinessLoading ? '#f3f4f6' : accountReady ? '#d1fae5' : '#fee2e2',
      border: `1px solid ${readinessLoading ? '#e5e7eb' : accountReady ? '#6ee7b7' : '#fecaca'}`,
      cursor: readinessLoading ? 'wait' : 'help',
      title: readinessLoading
        ? 'Loading account status...'
        : accountReady
          ? 'Stripe connected ✓ · Verification approved ✓'
          : `Stripe: ${stripeConnected ? 'Connected ✓' : 'Not connected'}, Verification: ${verificationState === 'approved' ? 'Approved ✓' : verificationState === 'pending' ? 'Pending' : 'Not submitted'}`
    }}>
      {readinessLoading ? 'Loading...' : accountReady ? 'Ready to bid' : 'Setup required'}
    </span>
  );
  // CB:END unified-status-badge

  const [menuOpen, setMenuOpen] = useState(false);
  const { isMobileLayout } = useDevice();

  return (
          <div className="header sticky top-0 z-40" style={{ background: '#123c3b', color: 'white', padding: isMobileLayout ? '12px 16px' : '20px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 40, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
      {/* CB:BEGIN safe-area-top-spacer */}
      <div className="sr-only" style={{ paddingTop: 'env(safe-area-inset-top)' }} />
      {/* CB:END safe-area-top-spacer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobileLayout ? 12 : 32 }}>
        <div className="logo" style={{ fontSize: isMobileLayout ? 20 : 24, fontWeight: 600, letterSpacing: -0.5, cursor: 'pointer', lineHeight: 1 }} onClick={() => onNavigate('landing')}>CoreBids</div>
        {!isMobileLayout && (
          <SignedIn>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              {contractorNavButtons}
            </div>
          </SignedIn>
        )}
      </div>
      {!isMobileLayout && (
      <div className="nav-buttons" style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
        <SignedIn>
          <div style={{ marginRight: 8 }}>
            {accountStatusBadge}
          </div>
          <span style={{ color: 'rgba(255,255,255,0.8)' }}>Welcome, {user?.firstName || user?.fullName?.split(' ')[0] || 'User'}</span>
          {/* CB:BEGIN fr-001-userbutton-ref-wrapper */}
          <div ref={userButtonContainerRef} data-user-button-container="true" style={{ display: 'inline-block' }}>
          {/* CB:END fr-001-userbutton-ref-wrapper */}
          <UserButton afterSignOutUrl="/" userProfileMode="modal">
            <UserButton.UserProfilePage label="Business Address" url="business-address" labelIcon={<span>🏢</span>}>
              <ContractorBusinessTab />
            </UserButton.UserProfilePage>
            <UserButton.UserProfilePage label="Payments" url="payments" labelIcon={<span>💳</span>}>
              <PaymentsTab />
            </UserButton.UserProfilePage>
            {/* CB:BEGIN fr-001-corebids-settings-pages */}
            <UserButton.UserProfilePage label="CoreBids Notifications" url="corebids-notifications" labelIcon={<span>🔔</span>}>
              <CoreBidsNotificationsTab />
            </UserButton.UserProfilePage>
            <UserButton.UserProfilePage label="CoreBids Privacy" url="corebids-privacy" labelIcon={<span>🔒</span>}>
              <CoreBidsPrivacyTab />
            </UserButton.UserProfilePage>
            <UserButton.UserProfilePage label="CoreBids Contractor Preferences" url="corebids-contractor-preferences" labelIcon={<span>⚙️</span>}>
              <CoreBidsContractorPreferencesTab />
            </UserButton.UserProfilePage>
            {/* CB:END fr-001-corebids-settings-pages */}
          </UserButton>
          {/* CB:BEGIN fr-001-userbutton-ref-wrapper-close */}
          </div>
          {/* CB:END fr-001-userbutton-ref-wrapper-close */}
        </SignedIn>
        <SignedOut>
          {/* CB:BEGIN contractor-header-mobile */}
          <SignInButton mode="modal">
            <button className="btn btn-secondary" style={{ color: 'black', fontSize: device.isMobileLayout ? '13px' : '14px', padding: device.isMobileLayout ? '6px 10px' : '8px 16px' }}>Login</button>
          </SignInButton>
          {/* CB:END contractor-header-mobile */}
        </SignedOut>
      </div>
      )}
      {/* Mobile hamburger */}
      {isMobileLayout && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button aria-label="Menu" onClick={() => setMenuOpen(true)} className="btn btn-secondary" style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
            ☰
          </button>
        </div>
      )}

      {isMobileLayout && (
      <BottomSheet isOpen={menuOpen} onClose={() => setMenuOpen(false)} title="Menu">
        <div style={{ display: 'grid', gap: 12 }}>
          <SignedIn>
              <div style={{ display: 'grid', gap: 8 }}>
              {contractorNavButtons}
              <button className="btn btn-secondary" style={{ color: 'black' }} onClick={() => { setMenuOpen(false); onNavigate('contractor-dashboard'); }}>Dashboard</button>
              <div>{accountStatusBadge}</div>
            </div>
          </SignedIn>
          <SignedOut>
            <SignInButton mode="modal">
              <button className="btn btn-secondary" style={{ color: 'black' }}>Login</button>
            </SignInButton>
          </SignedOut>
        </div>
      </BottomSheet>
      )}

      {/* CB:BEGIN contractor-docs-modal-ui */}
      {showDocsModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowDocsModal(false)}
        >
          <div
            style={{ background: 'white', borderRadius: 12, padding: 24, width: 'min(640px, 92vw)', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 25px rgba(0,0,0,0.15)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>My Documents</h2>
              <button onClick={() => setShowDocsModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
            </div>
            {docsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading documents...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

                {/* Verification documents section */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Verification &amp; Profile</div>
                  {contractorDocs.length === 0 ? (
                    <div style={{ padding: '16px', background: '#f9fafb', borderRadius: 8, color: '#6b7280', fontSize: 14 }}>
                      No verification documents submitted yet. Complete your verification to submit license and insurance documents.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {contractorDocs.map((doc: any) => (
                        <div key={doc.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                              {DOC_TYPE_LABELS[doc.type] || doc.type || 'Document'}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {doc.fileName || 'Unnamed file'}{doc.sizeBytes ? ` · ${(doc.sizeBytes / 1024).toFixed(0)} KB` : ''}
                            </div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                              Uploaded {new Date(doc.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                            </div>
                          </div>
                          <button
                            onClick={() => viewContractorDoc(doc.id)}
                            style={{ flexShrink: 0, padding: '6px 14px', background: 'white', color: '#2563eb', border: '1px solid #2563eb', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                          >
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Project folders section */}
                {projectFolders.length > 0 && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Projects</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {projectFolders.map((folder: any) => {
                        const isOpen = expandedProjects.has(folder.projectId);
                        return (
                          <div key={folder.projectId} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                            <button
                              onClick={() => toggleProjectFolder(folder.projectId)}
                              style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: '#f9fafb', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                            >
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{folder.address}</div>
                                {folder.city && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{folder.city}</div>}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 12, color: '#6b7280' }}>{folder.items.length} doc{folder.items.length !== 1 ? 's' : ''}</span>
                                <span style={{ fontSize: 16, color: '#6b7280', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
                              </div>
                            </button>
                            {isOpen && (
                              <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {folder.items.map((item: any) => (
                                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontWeight: 500, fontSize: 14, color: '#374151' }}>{item.label}</div>
                                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                                        {new Date(item.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                        {item.sizeBytes ? ` · ${(item.sizeBytes / 1024).toFixed(0)} KB` : ''}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => viewContractorDoc(item.id, item.kind, item.kind === 'signing' ? item.id : undefined)}
                                      style={{ flexShrink: 0, padding: '4px 12px', background: 'white', color: '#2563eb', border: '1px solid #2563eb', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
                                    >
                                      View
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>
        </div>
      )}
      {/* CB:END contractor-docs-modal-ui */}
    </div>
  );
};

export default SharedHeaderContractor; 